/**
 * UpSurv 2.0 — the section model.
 *
 * A survey is a walk through sections. Most sections exist once per machine
 * room, but three of them repeat: Overhead and Pit repeat per hoistway, Lobbies
 * repeats per group (spec Q7–9, brief §5). Room setup (M6/M7) is therefore the
 * hinge — once the surveyor has defined groups and hoistways, the rest of the
 * survey is generated from that structure rather than typed out.
 *
 * `buildSections(room)` does that generation for one room; `buildSurveySections`
 * assembles the whole walk: the building section, every room's sections in room
 * order, then the two survey-level sections at the end — Fire Life Safety
 * (Amendment 6) and the ad hoc checklist last of all.
 *
 * The order fields produce the elevator hall indicator in the mockup — a fixed
 * default path the surveyor can jump around inside. Jumping is for sequence,
 * not skipping (spec §2.2), so nothing here ever removes a section.
 */

import type {
  CarRef,
  Group,
  Hoistway,
  MachineRoom,
  QuestionCondition,
  RecommendedShot,
  Scope,
  SectionInstance,
  SectionKind,
  SurveyDocument,
} from './types';
import { sectionInstanceId } from './types';

/**
 * The default fixed walk order (spec §2.2), matching the mockup's hall
 * indicator. `overhead` and `pit` expand to one section per hoistway and
 * `lobby` to one per group, in this relative position.
 */
export const SECTION_ORDER: readonly SectionKind[] = [
  'building',
  'room_setup',
  'electrical',
  'hvac',
  'general',
  'overhead',
  'pit',
  'lobby',
  'cab',
  'access',
  'fls',
  'adhoc',
];

/** Base surveyor-facing titles. Repeating sections get a scope suffix appended. */
export const SECTION_TITLES: Record<SectionKind, string> = {
  building: 'Building',
  room_setup: 'Room Setup',
  electrical: 'Electrical',
  hvac: 'HVAC',
  general: 'General',
  overhead: 'Overhead',
  pit: 'Pit',
  lobby: 'Lobbies',
  cab: 'Cab Interior',
  access: 'Access',
  fls: 'Fire Life Safety',
  adhoc: 'Ad Hoc Items',
};

/* ─────────────────────────────── shot lists ─────────────────────────────── */

/**
 * Recommended photos per section, verbatim from spec §2.4. These warn on
 * section close and are listed on the submitted survey when missing — they
 * never block (§4 rule 8): the pit may genuinely be locked out.
 *
 * Two shots are conditional; see SHOT_CONDITIONS for the predicates.
 */
export const SHOT_LISTS: Record<SectionKind, RecommendedShot[]> = {
  // Amendment 6 (2026-08-18) moved the two FACP shots off the building and onto
  // the Fire Life Safety section — see `fls` below. The building has none.
  building: [],
  room_setup: [],
  electrical: [
    { id: 'electrical.disconnects', label: 'Every disconnect' },
    // Amendment 5 removed `electrical.controller`. The controller is being
    // ripped out on every one of these jobs, so a photo of it priced nothing and
    // told the office nothing — and it was reaching the last look as one more
    // line of "missing". Its slot goes to the relays, which the FLS
    // subcontractor DOES quote from (F8).
    { id: 'electrical.fls_relays', label: 'Fire service relays' },
  ],
  hvac: [
    { id: 'hvac.existing_unit', label: 'Existing unit (if any)' },
    // Only prompted on a Mini-Split — chilled water has no such line. D-053
    // (Chip, 2026-10-27): labelled for the refrigerant line, as H3 now is —
    // was "Condensate route"; the id is unchanged.
    { id: 'hvac.condensate_route', label: 'Refrigerant line route', conditional: true },
  ],
  // Spec §2.4 calls this "Machine Room General". Amendment 8 (D-041) adds the
  // controller shots F15/F16 quote off of — the outside nameplate is required.
  general: [
    { id: 'general.wide_room', label: 'Wide room shot' },
    { id: 'general.door', label: 'Machine room door' },
    { id: 'general.controller', label: 'Controller — outside, with the nameplate', required: true },
    { id: 'general.controller_inside', label: 'Controller — inside the cabinet' },
  ],
  overhead: [{ id: 'overhead.top_of_hoistway', label: 'Top of hoistway' }],
  pit: [
    { id: 'pit.full_pit', label: 'Full pit from above', required: true },
    { id: 'pit.ladders', label: 'Ladder(s)' },
    { id: 'pit.pump_area', label: 'Pump area' },
  ],
  // Amendment 8 (D-041) adds the two detector shots the FLS company reads the
  // lobby device count off of. D-045 (Chip, 2026-09-11): the first of them is
  // labelled "Main Egress Lobby" — was "Main Egress detector"; same id, same
  // place on the vendor form (`lobbyDetectors`), same caption.
  lobby: [
    { id: 'lobby.main_egress_fixtures', label: 'Main Egress fixtures' },
    { id: 'lobby.typical_floor_fixture', label: 'Typical floor fixture' },
    { id: 'lobby.detector_egress', label: 'Main Egress Lobby' },
    { id: 'lobby.detector_typical', label: "A typical floor's detector" },
  ],
  // Only prompted when the crane flag (A1) is set.
  access: [{ id: 'access.staging_area', label: 'Staging area', conditional: true }],
  // Amendment 8 (D-041): one row per car — a room with three cars gets three of
  // each, `expandShots` is what turns this one entry into that many rows.
  cab: [
    { id: 'cab.cop', label: 'Car operating panel', perCar: true },
    { id: 'cab.certificate', label: 'Posted certificate', perCar: true },
  ],
  // Amendment 5 gave the survey these two shots; Amendment 6 gave them a section
  // of their own. The FLS company quotes off these two pictures. Still
  // recommended, never required — §4 rule 8 is not suspended because a photo
  // matters more than usual, and a panel behind a locked fire-control door is a
  // real Tuesday. `formerly` is their address until 2026-08-18: a record that
  // took them on the building screen (123 Smith Street did) is not told it did
  // not. Amendment 8 (D-041) adds the nameplate close-up, required — the
  // office's report stalled more than once for want of it.
  fls: [
    {
      id: 'fls.facp',
      label: 'Fire alarm panel (FACP)',
      formerly: { sectionInstanceId: 'building', id: 'building.facp' },
    },
    {
      id: 'fls.facp_sticker',
      label: 'Close-up of the FACP service sticker',
      formerly: { sectionInstanceId: 'building', id: 'building.facp_sticker' },
    },
    { id: 'fls.facp_nameplate', label: "Close-up of the panel's make/model label", required: true },
  ],
  adhoc: [],
};

/** `cab.cop:car-3` → `cab.cop`. The ONE place the per-car suffix is stripped. */
export function shotBaseId(shotListId: string): string {
  const at = shotListId.indexOf(':');
  return at < 0 ? shotListId : shotListId.slice(0, at);
}

/** `cab.cop:car-3` → `car-3`; undefined for a shot that is not per car. */
export function shotCarId(shotListId: string): string | undefined {
  const at = shotListId.indexOf(':');
  return at < 0 ? undefined : shotListId.slice(at + 1);
}

/** A section's shot rows — a per-car shot becomes one row per car (Amendment 8). */
export function expandShots(kind: SectionKind, cars: readonly CarRef[]): RecommendedShot[] {
  return SHOT_LISTS[kind].flatMap((shot) =>
    shot.perCar
      ? cars.map((car) => ({ ...shot, id: `${shot.id}:${car.id}`, label: `${shot.label} — ${car.label}` }))
      : [{ ...shot }],
  );
}

/**
 * Does this photo cover this shot of this section? — the ONE definition.
 *
 * Read by `missingShots` (the record) and `sectionCloseView` (the screen), so
 * got and missing are one answer wherever they are read. A photo covers a shot
 * when it is filed on the section under the shot's id, or — Amendment 6 — under
 * the address the shot used to have.
 */
export function photoCoversShot(
  photo: { sectionInstanceId: string; shotListId?: string },
  section: { id: string },
  shot: RecommendedShot,
): boolean {
  if (photo.sectionInstanceId === section.id && photo.shotListId === shot.id) return true;
  return (
    shot.formerly !== undefined &&
    photo.sectionInstanceId === shot.formerly.sectionInstanceId &&
    photo.shotListId === shot.formerly.id
  );
}

/**
 * The predicate behind each `conditional: true` shot. RecommendedShot carries
 * only a boolean, so the condition lives here keyed by shot ID — same shape the
 * question catalog uses for `showWhen`, so one evaluator handles both.
 */
export const SHOT_CONDITIONS: Record<string, QuestionCondition> = {
  'hvac.condensate_route': { questionId: 'H1a', equals: 'minisplit' },
  'access.staging_area': { questionId: 'A1', equals: true },
};

/* ─────────────────────── what a section is made of ─────────────────────── */

/**
 * Sections that belong to the SURVEY rather than to a machine room — **D-023**.
 *
 * The design authority, live on production, 2026-08-07:
 *
 *   *"Under Ad Hoc Items it says 'Nothing is asked in this section for this
 *    room' — this feels like an error."*
 *
 * It was not an error and it was not about a room. Ad Hoc Items has no room, and
 * it has no catalog questions either: specification §2.5 says its five items —
 * storage rental, dumpster, clean pit of oil and debris WITH a quantity, parking,
 * and a free-form other with photos — are job-level extras the office quantifies
 * from the schedule, which is why they live on `SurveyDocument.adHoc` as
 * structure rather than as answers in §3. Walking it through the generic question
 * renderer therefore found nothing and, having been handed a room-shaped
 * sentence, blamed a room that was never involved.
 *
 * These two lists are the fix, stated once here, where the walk is generated:
 * which sections belong to no room, and which sections are not asked out of the
 * question catalog at all. A screen consults them instead of testing a kind by
 * hand, so a new checklist section cannot reintroduce the same empty list.
 *
 * Fire Life Safety joined the survey-level list on 2026-08-18 (Amendment 6): it
 * is asked once per job, generated once, and — unlike Ad Hoc — asked out of the
 * catalog (F1–F5), so it walks through the ordinary section screen.
 */
export const SURVEY_LEVEL_SECTIONS: readonly SectionKind[] = ['building', 'fls', 'adhoc'];

/** True when this section belongs to the whole job, not to one machine room. */
export function isSurveyLevel(kind: SectionKind): boolean {
  return SURVEY_LEVEL_SECTIONS.includes(kind);
}

/**
 * Sections whose body is a checklist of its own rather than catalog questions.
 *
 * Everything NOT in here must have at least one ungated question in every form
 * set it can be generated for — an empty generated section is either a
 * generation bug or a section that should not exist. `tests/unit/adhoc.test.ts`
 * holds the catalog to that.
 */
export const CHECKLIST_SECTIONS: readonly SectionKind[] = ['adhoc'];

/** True when this section's questions come from the §3 catalog. */
export function asksCatalogQuestions(kind: SectionKind): boolean {
  return !CHECKLIST_SECTIONS.includes(kind);
}

/**
 * Sections whose body is the ENTITY TREE — cars, groups, hoistways — and not a
 * list of question rows.
 *
 * ── Chip, field test, 2026-08-18 ────────────────────────────────────────────
 *   *"The subsequent screen flagged an error, indicating that car 4 wasn't in
 *    a hoistway yet. There was no option to edit or correct the error, only the
 *    message appeared. … we could not return to the room-setup screen."*
 *
 * `room_setup` is a section in the walk — the hall indicator lists it, and
 * Previous from Electrical lands on it — and every generic producer of a
 * section link handed it to the generic section screen, which asked the catalog
 * for its questions and rendered M1–M7 as ordinary rows: a hoistway multiselect
 * with no options, a car list with no cars, and §4 rule 7's sentence in red
 * under an empty control. The screen that could actually place Car 4 — the
 * tap-to-assign builder — was reachable only through the crumb and a ghost
 * button, and nothing that carried the sentence linked to it.
 *
 * So the kind is declared HERE, beside the walk generator, the way the checklist
 * and survey-level kinds are: a screen consults the declaration and renders the
 * setup builder as this section's body, instead of testing the kind by hand and
 * forgetting one call site. Its two ordinary questions (M4, M5) still come from
 * the catalog through the shared renderer — `asksCatalogQuestions` is true —
 * so they look and behave the way the same questions do everywhere else.
 *
 * A structure section is never FLAGGABLE: you cannot be locked out of a list
 * you are the one writing, and a "Room Setup is incomplete — …" gap would mean
 * nothing to Chip or Tim.
 */
export const STRUCTURE_SECTIONS: readonly SectionKind[] = ['room_setup'];

/** True when this section's body is the entity tree, rendered by the setup builder. */
export function isStructureSection(kind: SectionKind): boolean {
  return STRUCTURE_SECTIONS.includes(kind);
}

/* ──────────────────────────────── labels ────────────────────────────────── */

const HOISTWAY_PREFIX = /^(?:hoistway|shaft|hw)\b[\s·:.\-#]*/i;
const GROUP_PREFIX = /^(?:group|grp|bank)\b[\s·:.\-#]*/i;

/**
 * Squeeze an entity name into a section title suffix.
 *
 * "Hoistway 2" and "2" both become "HW 2" — the indicator row is narrow and the
 * mockup establishes the abbreviated form. A descriptive name the surveyor chose
 * deliberately ("North Shaft") is left alone rather than mangled.
 */
function shortLabel(name: string, prefix: RegExp, abbrev: string): string {
  const raw = name.trim();
  if (!raw) return abbrev;
  const stripped = raw.replace(prefix, '').trim();
  if (stripped !== raw) return stripped ? `${abbrev} ${stripped}` : abbrev;
  return /^[A-Za-z0-9]{1,3}$/.test(raw) ? `${abbrev} ${raw}` : raw;
}

/** "Hoistway 2" → "HW 2". */
export function hoistwayLabel(hoistway: Hoistway): string {
  return shortLabel(hoistway.name, HOISTWAY_PREFIX, 'HW');
}

/** "Group B" → "Grp B". */
export function groupLabel(group: Group): string {
  return shortLabel(group.name, GROUP_PREFIX, 'Grp');
}

/**
 * Section titles are room-relative — the room name lives in the screen header.
 * Anywhere a title travels away from that context (a Gap in the submission
 * notification, for one), qualify it with the room.
 */
export function qualifyTitle(roomName: string, title: string): string {
  return `${roomName} · ${title}`;
}

/* ────────────────────────────── generation ──────────────────────────────── */

/**
 * Every section for one machine room, in walk order.
 *
 * M hoistways ⇒ M Overhead + M Pit sections; N groups ⇒ N Lobbies sections
 * (brief §5). All Overheads come before all Pits, matching the mockup — the
 * surveyor works the tops of the shafts, then the bottoms.
 *
 * `startOrder` lets buildSurveySections lay several rooms end to end on one
 * monotonic order axis; call it with one argument for a room in isolation.
 */
export function buildSections(room: MachineRoom, startOrder = 0): SectionInstance[] {
  const sections: SectionInstance[] = [];
  let order = startOrder;

  const push = (kind: SectionKind, scope: Scope, title: string): void => {
    sections.push({
      id: sectionInstanceId(kind, scope),
      kind,
      scope,
      title,
      order: order++,
      // Expanded (and copied) so a surveyor's per-section shot state can never
      // leak across hoistways or rooms through a shared array, and a per-car
      // shot becomes one row per car in THIS room (Amendment 8).
      shotList: expandShots(kind, room.cars),
    });
  };

  const roomScope: Scope = { roomId: room.id };

  push('room_setup', roomScope, SECTION_TITLES.room_setup);
  push('electrical', roomScope, SECTION_TITLES.electrical);
  push('hvac', roomScope, SECTION_TITLES.hvac);
  push('general', roomScope, SECTION_TITLES.general);

  for (const hoistway of room.hoistways) {
    push(
      'overhead',
      { roomId: room.id, hoistwayId: hoistway.id },
      `${SECTION_TITLES.overhead} · ${hoistwayLabel(hoistway)}`,
    );
  }
  for (const hoistway of room.hoistways) {
    push(
      'pit',
      { roomId: room.id, hoistwayId: hoistway.id },
      `${SECTION_TITLES.pit} · ${hoistwayLabel(hoistway)}`,
    );
  }
  for (const group of room.groups) {
    push(
      'lobby',
      { roomId: room.id, groupId: group.id },
      `${SECTION_TITLES.lobby} · ${groupLabel(group)}`,
    );
  }

  push('cab', roomScope, SECTION_TITLES.cab);
  push('access', roomScope, SECTION_TITLES.access);

  return sections;
}

/**
 * The whole survey walk: building info once, then each room's sections in room
 * order, then Fire Life Safety, then the ad hoc checklist last — it is the
 * end-of-survey step the surveyor hits on the way to Submit (spec §2.5).
 *
 * A survey with no rooms yet (an assignment that arrived as bare an address —
 * spec §2.1) still yields Building, Fire Life Safety and Ad Hoc, so the walk is
 * never empty.
 *
 * **The two survey-level sections are pushed HERE and nowhere else** — outside
 * the room loop, with an empty scope, so a job with five machine rooms has one
 * FLS section and one ad hoc checklist and not five of each (D-023, and now
 * Amendment 6). `buildSections()` builds a ROOM and must never add them; their
 * ids carry no scope segments, which is what makes the close screen, the wizard
 * and the walk address the same section rather than copies.
 *
 * ── Why FLS is second to last, not last (Chip, 2026-08-18) ─────────────────
 *   *"Place it among the final sections of the survey, ideally the last, so the
 *    Surveyor can close and submit only after completing it."*
 * The gate is what makes it "only after" — `guidePlan` withholds Close & submit
 * until the section is walked (state/guide.ts). Its place in the ORDER is after
 * every room and before Ad Hoc, because spec §2.5 makes the extras checklist
 * the step on the way to Submit and D-023 shows it again on the close screen,
 * so it is the one section that need not be walked to be seen. FLS is the last
 * section the surveyor must stand in. Swapping the two lines below is the whole
 * cost of the other reading.
 */
export function buildSurveySections(doc: SurveyDocument): SectionInstance[] {
  const sections: SectionInstance[] = [];
  let order = 0;

  sections.push({
    id: sectionInstanceId('building'),
    kind: 'building',
    scope: {},
    title: SECTION_TITLES.building,
    order: order++,
    shotList: expandShots('building', []),
  });

  for (const room of doc.rooms) {
    const roomSections = buildSections(room, order);
    sections.push(...roomSections);
    order += roomSections.length;
  }

  sections.push({
    id: sectionInstanceId('fls'),
    kind: 'fls',
    scope: {},
    title: SECTION_TITLES.fls,
    order: order++,
    shotList: expandShots('fls', []),
  });

  sections.push({
    id: sectionInstanceId('adhoc'),
    kind: 'adhoc',
    scope: {},
    title: SECTION_TITLES.adhoc,
    order: order++,
    shotList: expandShots('adhoc', []),
  });

  return sections;
}
